<template>
    <Card pageTitle="New Quantity Adjustment">
        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
            <BackNavigateBtn title="Back to Qty Adjustments" to="/inventory-adjustments/quantity" />

            <div class="create-adjustment-container">
                <!-- Enhanced Product Search Section -->
                <div class="product-search-section">
                    <div class="section-header">
                        <h4><i class="fas fa-search mr-2"></i>Add Products to Adjust</h4>
                        <p class="section-subtitle">Search and select products to adjust their quantities</p>
                    </div>
                    <ProductSearchAndAdder :addedProducts="addedProducts" @add="handleAdd" />
                </div>

                <!-- Enhanced Products Table -->
                <div class="products-table-section">
                    <div class="section-header">
                        <h4><i class="fas fa-list mr-2"></i>Products for Adjustment</h4>
                        <p class="section-subtitle">Review and adjust quantities for selected products</p>
                    </div>
                    
                    <div class="products-table-container">
                        <div class="table-responsive">
                            <table class="custom-products-table">
                                <thead>
                                    <tr>
                                        <th class="action-col">
                                            <i class="fas fa-times"></i>
                                        </th>
                                        <th class="product-info-col">
                                            <i class="fas fa-box mr-1"></i>Product Details
                                        </th>
                                        <th class="location-info-col">
                                            <i class="fas fa-warehouse mr-1"></i>Location
                                        </th>
                                        <th class="stock-adjustment-col">
                                            <i class="fas fa-cubes mr-1"></i>Stock & Adjustment
                                        </th>
                                        <th class="reason-notes-col">
                                            <i class="fas fa-comment mr-1"></i>Reason & Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="product of addedProducts" :key="product.uniqueId" class="product-row">
                                        <td class="action-cell">
                                            <button @click="removeProduct(product)" class="remove-btn">
                                                <i class="fas fa-times" />
                                            </button>
                                        </td>
                                        <td class="product-info-cell">
                                            <div class="product-details">
                                                <div class="product-name">{{ product.name }}</div>
                                                <div class="product-meta">
                                                    <span class="sku-badge">{{ product.supplier_sku }}</span>
                                                    <span class="batch-info">{{ product.selectedPackage.title }}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="location-info-cell">
                                            <div class="location-details">
                                                <div class="location-badge">{{ product.selectedWarehouse.title }}</div>
                                                <div class="room-badge" :class="product.selectedRoom.title.includes('Front') ? 'front-room' : 'back-room'">
                                                    {{ product.selectedRoom.title.replace("Room", "") }}
                                                </div>
                                            </div>
                                        </td>
                                        <td class="stock-adjustment-cell">
                                            <div class="stock-adjustment-container">
                                                <div class="stock-row">
                                                    <span class="stock-label">Current:</span>
                                                    <span class="stock-value">{{ product.stockQty }}</span>
                                                </div>
                                                <div class="adjustment-row">
                                                    <span class="adjust-label">Adjust:</span>
                                                    <CustomInput 
                                                        type="number" 
                                                        small 
                                                        v-model="product.adjustQty" 
                                                        @input="updateNewQtyFromAdjustQty(product)"
                                                        :class="getAdjustmentClass(product.adjustQty)"
                                                        class="compact-input"
                                                    />
                                                </div>
                                                <div class="new-qty-row">
                                                    <span class="new-qty-label">New:</span>
                                                    <CustomInput 
                                                        type="number" 
                                                        small 
                                                        v-model="product.newQty" 
                                                        @input="updateAdjustQtyFromNewQty(product)"
                                                        class="compact-input"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td class="reason-notes-cell">
                                            <div class="reason-notes-container">
                                                <div class="reason-row">
                                                    <SelectDropdown
                                                        v-if="reasonCodes.length"
                                                        normal
                                                        :value="product.reasonCode"
                                                        :options="reasonCodes"
                                                        @change="product.reasonCode = $event"
                                                        class="compact-select"
                                                    />
                                                </div>
                                                <div class="notes-row">
                                                    <CustomInput 
                                                        small 
                                                        v-model="product.notes" 
                                                        placeholder="Notes..."
                                                        class="compact-input"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <div v-if="!addedProducts.length" class="empty-products-state">
                            <div class="empty-icon">
                                <i class="fas fa-box-open"></i>
                            </div>
                            <h5>No Products Added Yet</h5>
                            <p>Use the search above to find and add products for quantity adjustment</p>
                        </div>
                    </div>
                </div>

                <!-- Enhanced Action Buttons -->
                <div class="action-buttons-section">
                    <div class="buttons-container">
                        <router-link to="/inventory-adjustments/quantity">
                            <Button class="cancel-btn">
                                <i class="fas fa-times mr-2"></i>Cancel
                            </Button>
                        </router-link>
                        <Button 
                            @click="openConfirmationModal" 
                            :disabled="!addedProducts.length || updatingQtyAdjustment"
                            class="save-btn"
                            primary
                        >
                            <i class="fas fa-save mr-2"></i>
                            {{ !updatingQtyAdjustment ? "Review & Save" : "Saving..." }}
                        </Button>
                    </div>
                    
                    <div v-if="addedProducts.length" class="adjustment-summary">
                        <div class="summary-item">
                            <span class="summary-label">Products:</span>
                            <span class="summary-value">{{ addedProducts.length }}</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Total Adjustments:</span>
                            <span class="summary-value" :class="getTotalAdjustmentClass()">
                                {{ getTotalAdjustment() > 0 ? '+' : '' }}{{ getTotalAdjustment() }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
                
                <!-- Confirmation Modal -->
                <CustomModal :show="showConfirmationModal" title="Confirm Quantity Adjustment" @close="closeConfirmationModal">
                    <div class="uniform-confirmation-content">
                        <!-- Summary Header -->
                        <div class="confirmation-summary-header">
                            <div class="summary-stats">
                                <div class="stat-item">
                                    <span class="stat-label">Total Items</span>
                                    <span class="stat-value">{{ addedProducts.length }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Net Adjustment</span>
                                    <span class="stat-value" :class="getTotalAdjustmentClass()">
                                        {{ getTotalAdjustment() > 0 ? '+' : '' }}{{ getTotalAdjustment() }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Product Cards -->
                        <div class="confirmation-products">
                            <div v-for="product in addedProducts" :key="product.uniqueId" class="product-confirmation-card">
                                <div class="product-card-header">
                                    <div class="product-title">
                                        <h5>{{ product.name }}</h5>
                                        <span class="product-sku">{{ product.supplier_sku }}</span>
                                    </div>
                                    <div class="adjustment-badge" :class="getAdjustmentBadgeClass(product.adjustQty)">
                                        <i :class="getAdjustmentIcon(product.adjustQty)"></i>
                                        {{ product.adjustQty > 0 ? '+' : '' }}{{ product.adjustQty }}
                                    </div>
                                </div>
                                
                                <div class="product-card-body">
                                    <div class="detail-grid">
                                        <div class="detail-item">
                                            <span class="detail-label">Location</span>
                                            <span class="detail-value">{{ product.selectedWarehouse.title }} / {{ product.selectedRoom.title }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">Current Stock</span>
                                            <span class="detail-value">{{ product.stockQty }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">New Quantity</span>
                                            <span class="detail-value">{{ product.newQty }}</span>
                                        </div>
                                        <div class="detail-item" v-if="product.reasonCode">
                                            <span class="detail-label">Reason</span>
                                            <span class="detail-value">{{ getReasonName(product.reasonCode) }}</span>
                                        </div>
                                        <div class="detail-item full-width" v-if="product.notes">
                                            <span class="detail-label">Notes</span>
                                            <span class="detail-value">{{ product.notes }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="uniform-confirmation-actions">
                            <Button @click="closeConfirmationModal" outline>
                                <i class="fas fa-times mr-2"></i>Cancel
                            </Button>
                            <Button @click="handleSave" :disabled="updatingQtyAdjustment" primary>
                                <i class="fas fa-check mr-2"></i>
                                {{ !updatingQtyAdjustment ? "Confirm Adjustment" : "Saving..." }}
                            </Button>
                        </div>
                    </div>
                </CustomModal>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import ProductSearchAndAdder from "../../../components/product/ProductSearchAndAdder.vue"
import ProductSearchInput from "../../../components/product/ProductSearchInput.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import { http } from "../../../utils/http"
import QuantityFetchApi from "../../../utils/fetch/quantity-fetch-api"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import { errMsg } from "../../../utils/common"
import BackNavigateBtn from "../../../components/UI/BackNavigateBtn.vue"
import CustomModal from "../../../components/layout/CustomModal.vue"
import ScannerMixin from "@/mixins/scanner-mixin"

export default {
    mixins: [ScannerMixin],
    data() {
        return {
            selectedProduct: null,
            selectedWarehouse: null,
            selectedRoom: null,
            selectedPackage: null,
            addedProducts: [],
            reasonCodes: [],
            updatingQtyAdjustment: false,
            PERMISSION_CODES,
            showConfirmationModal: false
        }
    },
    components: {
        ProductSearchAndAdder,
        CustomInput,
        Card,
        Button,
        SelectDropdown,
        CustomTable,
        ProductSearchInput,
        AllowPermission,
        BackNavigateBtn,
        CustomModal
    },
    async mounted() {
        await this.fetchReasonCodes()
    },
    computed: {
        ...mapState("misc", ["warehouses", "warehouseRooms"]),
    },
    methods: {
        getNewQty(product) {
            return Number(product.stockQty) + Number(product.adjustQty || 0)
        },
        updateNewQtyFromAdjustQty(product) {
            // When adjustQty changes, update newQty
            product.newQty = Number(product.stockQty) + Number(product.adjustQty || 0)
        },
        updateAdjustQtyFromNewQty(product) {
            // When newQty changes, update adjustQty
            product.adjustQty = Number(product.newQty || 0) - Number(product.stockQty)
        },
        async fetchReasonCodes() {
            try {
                const res = await http.get("/reason-codes")
                this.reasonCodes = res.data.result.map((item) => ({ ...item, title: item.name }))
            } catch (err) {
                console.log("error in fetching reason codes", err)
            }
        },
        openConfirmationModal() {
            if (this.addedProducts.length === 0) {
                this.$toast.error("Please add at least one product for adjustment")
                return
            }
            this.showConfirmationModal = true
        },
        
        closeConfirmationModal() {
            this.showConfirmationModal = false
        },
        
        getReasonName(reasonId) {
            const reason = this.reasonCodes.find(r => r.id === reasonId)
            return reason ? reason.title : ''
        },
        
        async handleSave() {
            if (this.updatingQtyAdjustment) return
            const postData = this.addedProducts.map((item) => ({
                item_code: item.item_code,
                item_id: item.id,
                item_name: item.name,
                supplier_sku: item.supplier_sku,
                batch_no: item.selectedLedgerEntry.batch_no,
                warehouse_id: item.selectedLedgerEntry.warehouse,
                is_front_room: item.fromFrontRoom,
                stock_qty: item.stockQty,
                adjust_qty: item.adjustQty,
                new_qty: Number(item.newQty),
                unit: "Single Unit",
                reason_code_id: item.reasonCode,
                notes: item.notes,
                selling_price: item.selling_price,
                last_purchase_rate: item.last_purchase_rate,
            }))

            this.updatingQtyAdjustment = true
            try {
                const data = await QuantityFetchApi.createQuantityAdjustment(postData)
                this.$toast.success("Quantity adjustment completed successfully!")
                this.closeConfirmationModal()
                
                // Navigate to the newly created adjustment details if we have an ID
                if (data.result && data.result.name) {
                    this.$router.push(`/inventory-adjustments/quantity/${data.result.name}`)
                } else {
                    this.$router.push("/inventory-adjustments/quantity")
                }
            } catch (err) {
                console.error("Error creating quantity adjustment:", err)
                this.$toast.error(errMsg(err, "Error in Quantity adjustment!"))
            }
            this.updatingQtyAdjustment = false
        },
        searchProductCondition(item, prodId = "") {
            const cond1 = item.id == (prodId || this.selectedProduct.id)
            const cond2 = item.selectedWarehouse.id == this.selectedWarehouse
            const cond3 = item.selectedRoom.id == this.selectedRoom
            const cond4 = item.selectedPackage.id == this.selectedPackage
            console.log("cond1", cond1)
            console.log("cond2", cond2)
            console.log("cond3", cond3)
            console.log("cond4", cond4)

            return cond1 && cond2 && cond3 && cond4
        },
        handleAdd(product) {
            if (product) {
                if (this.addedProducts.find((item) => this.searchProductCondition(item, product))) {
                    this.$toast.error("Product alreay added!")
                } else {
                    // Set initial newQty value when adding product
                    const newProduct = { 
                        ...product, 
                        uniqueId: Date.now(),
                        newQty: Number(product.stockQty) + Number(product.adjustQty || 0)
                    }
                    this.addedProducts.push(newProduct)
                }
            }
        },
        async onScanReceived(scanData) {
            try {
                const res = await http.get(`/stock/inventory?variants=true&search=${encodeURIComponent(scanData)}&limit=1&return_ledger_entries=true`)
                const product = (res.data?.result || [])[0]
                if (!product) {
                    this.$toast.error("No matching product found for scan")
                    return
                }
                const led = (product.ledgerEntries || [])[0]
                if (!led) {
                    this.$toast.error("No stock entries for scanned product")
                    return
                }
                const warehouse = this.warehouses.find(w => w.id == led.warehouse)
                const room = Object.values(this.warehouseRooms)[0]
                const fromFrontRoom = room?.id == this.warehouseRooms.FRONT_ROOM.id
                const stockQty = fromFrontRoom ? Number(led.stock_value) : Number(led.stock_value_back_room)

                const newProduct = {
                    ...product,
                    selectedLedgerEntry: led,
                    selectedWarehouse: { id: warehouse?.id, title: warehouse?.name },
                    selectedRoom: room,
                    selectedPackage: { id: led.batch_no, title: `${led.batch_no} (${stockQty})` },
                    fromFrontRoom,
                    stockQty,
                    uniqueId: Date.now(),
                    newQty: Number(stockQty),
                    adjustQty: 0,
                }
                this.addedProducts.push(newProduct)
                this.$toast.success("Product added via scan!")
            } catch (err) {
                console.log("scan add error", err)
                this.$toast.error("Scan failed to add product")
            }
        },
        removeProduct(product) {
            this.addedProducts = this.addedProducts.filter((item) => item.uniqueId != product.uniqueId)
            this.$toast.success("Removed product!")
        },
        getAdjustmentClass(adjustQty) {
            if (adjustQty > 0) return 'positive-adjustment'
            if (adjustQty < 0) return 'negative-adjustment'
            return 'neutral-adjustment'
        },
        getTotalAdjustment() {
            return this.addedProducts.reduce((total, product) => {
                return total + (Number(product.adjustQty) || 0)
            }, 0)
        },
        getTotalAdjustmentClass() {
            const total = this.getTotalAdjustment()
            if (total > 0) return 'positive-total'
            if (total < 0) return 'negative-total'
            return 'neutral-total'
        },
        getAdjustmentBadgeClass(adjustQty) {
            if (adjustQty > 0) return 'positive-badge'
            if (adjustQty < 0) return 'negative-badge'
            return 'neutral-badge'
        },
        getAdjustmentIcon(adjustQty) {
            if (adjustQty > 0) return 'fas fa-arrow-up'
            if (adjustQty < 0) return 'fas fa-arrow-down'
            return 'fas fa-minus'
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.create-adjustment-container {
    max-width: 1400px;
    margin: 0 auto;
}

.section-header {
    margin-bottom: 20px;
    
    h4 {
        color: #0D2D2C;
        font-weight: 600;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        
        i {
            color: #BA8E58;
            font-size: 1.1rem;
        }
    }
    
    .section-subtitle {
        color: #6c757d;
        font-size: 0.9rem;
        margin: 0;
    }
}

.product-search-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e9ecef;
}

.products-table-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e9ecef;
}

.products-table-container {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e9ecef;
}

.custom-products-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    table-layout: fixed;
    
    thead {
        background: linear-gradient(135deg, #0D2D2C 0%, #1a4a47 100%);
        
        th {
            padding: 14px 10px;
            text-align: left;
            font-weight: 600;
            color: white;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: none;
            
            i {
                opacity: 0.8;
                font-size: 0.75rem;
            }
            
            &.action-col { width: 60px; text-align: center; }
            &.product-info-col { width: 25%; }
            &.location-info-col { width: 15%; }
            &.stock-adjustment-col { width: 25%; }
            &.reason-notes-col { width: 35%; }
        }
    }
    
    tbody {
        tr.product-row {
            border-bottom: 1px solid #f0f0f0;
            transition: all 0.2s ease;
            
            &:hover {
                background-color: #f8f9fa;
            }
            
            &:last-child {
                border-bottom: none;
            }
        }
        
        td {
            padding: 12px 10px;
            vertical-align: middle;
            border: none;
        }
    }
}

.action-cell {
    text-align: center;
    
    .remove-btn {
        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
        border: none;
        border-radius: 6px;
        color: white;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
            transform: scale(1.1);
            box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
        }
        
        i {
            font-size: 0.8rem;
        }
    }
}

.product-info-cell {
    .product-details {
        .product-name {
            font-weight: 600;
            color: #0D2D2C;
            font-size: 0.9rem;
            line-height: 1.3;
            margin-bottom: 4px;
        }
        
        .product-meta {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            
            .sku-badge {
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 0.75rem;
                font-weight: 500;
                color: #495057;
                border: 1px solid #dee2e6;
            }
            
            .batch-info {
                color: #6c757d;
                font-size: 0.75rem;
                background: #f8f9fa;
                padding: 2px 6px;
                border-radius: 3px;
            }
        }
    }
}

.location-info-cell {
    .location-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
        
        .location-badge {
            background: linear-gradient(135deg, #BA8E58 0%, #d4a574 100%);
            color: white;
            padding: 3px 6px;
            border-radius: 3px;
            font-size: 0.75rem;
            font-weight: 500;
            text-align: center;
        }
        
        .room-badge {
            padding: 3px 6px;
            border-radius: 3px;
            font-size: 0.75rem;
            font-weight: 500;
            text-align: center;
            
            &.front-room {
                background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                color: white;
            }
            
            &.back-room {
                background: linear-gradient(135deg, #8D443B 0%, #a0523d 100%);
                color: white;
            }
        }
    }
}

.stock-adjustment-cell {
    .stock-adjustment-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        
        .stock-row,
        .adjustment-row,
        .new-qty-row {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .stock-label,
            .adjust-label,
            .new-qty-label {
                font-size: 0.75rem;
                font-weight: 500;
                color: #6c757d;
                min-width: 55px;
            }
            
            .stock-value {
                font-weight: 600;
                color: #0D2D2C;
                background: #f8f9fa;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 0.8rem;
                border: 1px solid #e9ecef;
            }
            
            .compact-input {
                max-width: 70px !important;
                
                &.positive-adjustment {
                    border-color: #28a745;
                    background-color: rgba(40, 167, 69, 0.05);
                }
                
                &.negative-adjustment {
                    border-color: #dc3545;
                    background-color: rgba(220, 53, 69, 0.05);
                }
            }
        }
    }
}

.reason-notes-cell {
    .reason-notes-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        
        .reason-row {
            .compact-select {
                width: 100%;
                max-width: none;
            }
        }
        
        .notes-row {
            .compact-input {
                width: 100%;
                max-width: none;
            }
        }
    }
}

.empty-products-state {
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;
    
    .empty-icon {
        margin-bottom: 20px;
        
        i {
            font-size: 3rem;
            color: #dee2e6;
        }
    }
    
    h5 {
        color: #0D2D2C;
        margin-bottom: 8px;
        font-weight: 600;
    }
    
    p {
        margin: 0;
        font-size: 0.9rem;
    }
}

.action-buttons-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e9ecef;
    
    .buttons-container {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-bottom: 16px;
        
        .cancel-btn {
            background: #6c757d;
            border-color: #6c757d;
            
            &:hover {
                background: #5a6268;
                border-color: #545b62;
            }
        }
        
        .save-btn {
            background: linear-gradient(135deg, #0D2D2C 0%, #1a4a47 100%);
            border: none;
            
            &:hover:not(:disabled) {
                background: linear-gradient(135deg, #1a4a47 0%, #0D2D2C 100%);
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(13, 45, 44, 0.2);
            }
            
            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }
    
    .adjustment-summary {
        display: flex;
        justify-content: flex-end;
        gap: 24px;
        padding-top: 16px;
        border-top: 1px solid #e9ecef;
        
        .summary-item {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .summary-label {
                color: #6c757d;
                font-size: 0.9rem;
                font-weight: 500;
            }
            
            .summary-value {
                font-weight: 700;
                font-size: 1rem;
                
                &.positive-total {
                    color: #28a745;
                }
                
                &.negative-total {
                    color: #dc3545;
                }
                
                &.neutral-total {
                    color: #6c757d;
                }
            }
        }
    }
}

// Uniform Confirmation Modal Styles
.uniform-confirmation-content {
    display: flex;
    flex-direction: column;
    max-height: 75vh;
    overflow-y: auto;
}

.confirmation-summary-header {
    background: linear-gradient(135deg, $lightHoverColor 0%, lighten($lightHoverColor, 4%) 100%);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    border: 1px solid $lightBorderColor;
}

.summary-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 16px;
}

.stat-item {
    text-align: center;
    background: $lightCardBg;
    padding: 16px 12px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid $lightBorderColor;
    
    .stat-label {
        display: block;
        font-size: 0.75rem;
        font-weight: 600;
        color: $textColor;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }
    
    .stat-value {
        display: block;
        font-size: 1.3rem;
        font-weight: 800;
        color: $textColor;
        
        &.positive {
            color: $successColor;
        }
        
        &.negative {
            color: $dangerColor;
        }
        
        &.positive-total {
            color: #1e7e34;
        }
        
        &.negative-total {
            color: #bd2130;
        }
        
        &.neutral-total {
            color: #495057;
        }
    }
}

.confirmation-products {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
    max-height: 350px;
    overflow-y: auto;
    padding-right: 8px;
}

.product-confirmation-card {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.product-card-header {
    background: linear-gradient(135deg, $primaryColor 0%, darken($primaryColor, 8%) 100%);
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.product-title {
    h5 {
        color: white;
        margin: 0 0 4px 0;
        font-size: 1rem;
        font-weight: 600;
    }
    
    .product-sku {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.8rem;
        font-weight: 500;
    }
}

.adjustment-badge,
.price-change-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    
    &.positive-badge { background: rgba($successColor, 0.2); color: darken($successColor, 30%); border: 1px solid rgba($successColor, 0.3); }
    
    &.negative-badge { background: rgba($dangerColor, 0.2); color: darken($dangerColor, 25%); border: 1px solid rgba($dangerColor, 0.3); }
    
    &.neutral-badge {
        background: rgba(108, 117, 125, 0.2);
        color: #495057;
        border: 1px solid rgba(108, 117, 125, 0.3);
    }
    
    i {
        font-size: 0.75rem;
    }
}

.product-card-body {
    padding: 16px;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: rgba($lightHoverColor, 0.6);
    border-radius: 6px;
    border: 1px solid $lightBorderColor;
    
    &.full-width {
        grid-column: 1 / -1;
    }
    
    .detail-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: $textColor;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .detail-value {
        font-size: 0.95rem;
        font-weight: 700;
        color: $textColor;
        
        &.positive { color: $successColor; font-weight: 800; }
        &.negative { color: $dangerColor; font-weight: 800; }
    }
}

.uniform-confirmation-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid $lightBorderColor;
}

/* Dark overrides */
.dark .confirmation-summary-header { background: lighten($darkCardBg, 4%); border-color: $darkBorderColor; }
.dark .stat-item { background: $darkCardBg; border-color: $darkBorderColor; }
.dark .stat-item .stat-label { color: rgba($darkTextColor, 0.8); }
.dark .stat-item .stat-value { color: $darkTextColor; }
.dark .stat-item .stat-value.positive, .dark .stat-item .stat-value.positive-total { color: $successColorDark; }
.dark .stat-item .stat-value.negative, .dark .stat-item .stat-value.negative-total { color: $dangerColorDark; }
.dark .product-confirmation-card { background: $darkCardBg; border-color: $darkBorderColor; }
.dark .product-card-header { background: linear-gradient(135deg, $primaryColorDark 0%, darken($primaryColorDark, 8%) 100%); }
.dark .detail-item { background: lighten($darkCardBg, 3%); border-color: $darkBorderColor; }
.dark .detail-item .detail-label, .dark .detail-item .detail-value { color: $darkTextColor; }

// Responsive Design
@media (max-width: 1200px) {
    .custom-products-table {
        font-size: 0.8rem;
        
        thead th {
            padding: 12px 8px;
            font-size: 0.75rem;
        }
        
        tbody td {
            padding: 10px 8px;
        }
    }
}

/* Tokenized spacing and paddings overrides for consistency */
.section-header { margin-bottom: $sectionGap; }
.product-search-section,
.products-table-section,
.action-buttons-section {
    border-radius: $cardRadius;
    padding: $cardPaddingY $cardPaddingX;
    margin-bottom: $sectionGap;
    box-shadow: $cardShadow;
}
.products-table-container { border-radius: $cardRadius; }
.custom-products-table thead th { padding: $tableCellPaddingY $tableCellPaddingX; }
.custom-products-table tbody td { padding: $tableCellPaddingY $tableCellPaddingX; }

@media (max-width: 768px) {
    .create-adjustment-container {
        margin: 0 -10px;
    }
    
    .product-search-section,
    .products-table-section,
    .action-buttons-section {
        margin-left: 10px;
        margin-right: 10px;
        border-radius: 8px;
        padding: 16px;
    }
    
    .custom-products-table {
        font-size: 0.7rem;
        
        thead th {
            padding: 8px 4px;
            font-size: 0.65rem;
            
            i {
                display: none;
            }
        }
        
        tbody td {
            padding: 6px 4px;
        }
    }
    
    .stock-adjustment-cell {
        .stock-adjustment-container {
            gap: 4px;
            
            .stock-row,
            .adjustment-row,
            .new-qty-row {
                gap: 4px;
                
                .stock-label,
                .adjust-label,
                .new-qty-label {
                    min-width: 40px;
                    font-size: 0.65rem;
                }
                
                .compact-input {
                    max-width: 60px !important;
                }
            }
        }
    }
    
    .reason-notes-cell {
        .reason-notes-container {
            gap: 4px;
        }
    }
    
    .adjustment-summary {
        flex-direction: column;
        gap: 12px;
        
        .summary-item {
            justify-content: space-between;
        }
    }
    
    // Mobile responsive for confirmation modal
    .summary-stats {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
    
    .stat-item {
        padding: 12px 8px;
        
        .stat-label {
            font-size: 0.7rem;
        }
        
        .stat-value {
            font-size: 1rem;
        }
    }
    
    .detail-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
    
    .product-card-header {
        padding: 12px;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .product-card-body {
        padding: 12px;
    }
}
</style>
